export class CartItemCustomizations {
    customizationId: string;
    title: string;
    selectionPrice: number;
    selectionDeliveryPrice:number;
    selectionText:string;
    selectedOptions:{
        optionId:string,
        quantity:number,
        itemMultiplier:number, 
        toppingSide:"left"|"full"|"right"|null
    }[];

    //The following options will alter how the receipt is presented to the customer
    showTitleOnReceipt:boolean|null;
    prefixStringForItemOnReceipt:string|null;
    suffixStringForItemOnReceipt:string|null;
    hideFromReceipt:string|null;
    group:{
        groupTitle:string, 
        addToThisGroupIfCustomizationItemTitleStartsWith?:string // The '?' means the variable is optional
        hideCustomizationItemTitle?:boolean,
    }[]|null


    /**
     * Constructor
     * @param itemId Id of the customization
     * @param title title of the customization
     * @param price how much it costs
     * @param deliveryPrice how much it costs for delivery
     * @param selectionText text representing the customization items that were selected by the customer
     * @param selectedOptions Array of objects containing the IDs of the options selected, how many items were 
     * selected for each option, a multiplier (used for example, for double topping pizzas (mutiplier = 2)), and the "topping side" used to determine which
     * side of a pizza a topping goes to, if the client has this feature configured for them.
     */
    constructor(itemId: string, title: string, price: number, deliveryPrice:number, selectionText: string, 
        selectedOptions:{ optionId:string, quantity:number, itemMultiplier:number, toppingSide:"left"|"full"|"right"|null}[]) {
        this.customizationId = itemId;
        this.title = title;
        this.selectionPrice = price;
        this.selectionDeliveryPrice = deliveryPrice;
        this.selectionText = selectionText;
        this.selectedOptions = selectedOptions;

        this.showTitleOnReceipt = null;
        this.prefixStringForItemOnReceipt = null;
        this.suffixStringForItemOnReceipt = null;
        this.hideFromReceipt = null;
        this.group = null;
    }

    /**
     * Adds configurations related to how the receipt should be displayed.
     * @param showTitleOnReceipt If this customization should have it's title displayed on the receipt
     * @param prefixStringForItemOnReceipt prefix on the receipt for the item. For example, if we use #, the receipt will show "#nameOfTheItem"
     * @param suffixStringForItemOnReceipt sufix on the receipt for the item. For example, if we use #, the receipt will show "nameOfTheItem#"
     * @param hideFromReceipt determines which customization items should NOT show on the receipt. Usually the one selected by default. Just put it's ID here.
     * @param group Customizations under the same group will be displayed together.
     * So for example, if you have one product called "two pizza special", where you can select 2 pizzas, and you have:
     *     pizza 1 - crust
     *     pizza 1 - sauce
     *     pizza 1 - toppings
     * and 
     *     pizza 2 - crust
     *     pizza 2 - sauce
     *     pizza 2 - toppings
     * You could for example put all pizza 1 customizations on the "PIZZA 1" group and all the pizza 2 customizations on the "PIZZA 2" group
     * That way, when you printed the receipt, it would organize all those customizations together
     * ---
     * groupTitle = name of the group. Items with the same name will ber grouped together. 
     * 
     * addToThisGroupIfCustomizationItemTitleStartsWith = If the items on this customization can belong to different groups, you can use this variable to sort them.
     * If present, you should have multiple groups, one for each different "addToThisGroupIfCustomizationItemTitleStartsWith"
     * 
     * hideCustomizationItemTitle = If the customization title starts with this string, we will sort them into the group
     */
    addReceiptConfigs(showTitleOnReceipt:boolean|null,prefixStringForItemOnReceipt:string|null,
        suffixStringForItemOnReceipt:string|null, hideFromReceipt:string|null, group:{
            groupTitle:string, 
            addToThisGroupIfCustomizationItemTitleStartsWith:string
            hideCustomizationItemTitle:boolean,
        }[]|null){
        this.showTitleOnReceipt = showTitleOnReceipt;
        this.prefixStringForItemOnReceipt = prefixStringForItemOnReceipt;
        this.suffixStringForItemOnReceipt = suffixStringForItemOnReceipt;
        this.hideFromReceipt = hideFromReceipt;
        this.group = group;
    }

    getUsedSelectionPrice(isDelivery:boolean){
        return isDelivery?this.selectionDeliveryPrice:this.selectionPrice;
    }

    toAwsObject() {
        let cartItemObj: any = {
            "customizationId": this.customizationId,
            "title": this.title,
            "selectionPrice": this.selectionPrice,
            "selectionDeliveryPrice": this.selectionDeliveryPrice,
            "selectionText": this.selectionText,
            "selectedOptions":this.selectedOptions
        }

        if(this.showTitleOnReceipt!=null){
            cartItemObj["showTitleOnReceipt"] = this.showTitleOnReceipt;
        }
        if(this.prefixStringForItemOnReceipt!=null){
            cartItemObj["prefixStringForItemOnReceipt"] = this.prefixStringForItemOnReceipt;
        }
        if(this.suffixStringForItemOnReceipt!=null){
            cartItemObj["suffixStringForItemOnReceipt"] = this.suffixStringForItemOnReceipt;
        }
        if(this.hideFromReceipt!=null){
            cartItemObj["hideFromReceipt"] = this.hideFromReceipt;
        }
        if(this.group!=null){
            cartItemObj["group"] = this.group;
        }

        return cartItemObj;
    }

    static newFromAws(awsData:any){
        let customizations =  new CartItemCustomizations(
            awsData.customizationId,
            awsData.title,
            awsData.selectionPrice,
            awsData.selectionDeliveryPrice,
            awsData.selectionText,
            awsData.selectedOptions
        );

        customizations.addReceiptConfigs(
            awsData.showTitleOnReceipt,
            awsData.prefixStringForItemOnReceipt,
            awsData.suffixStringForItemOnReceipt,
            awsData.hideFromReceipt,
            awsData.group
        )

        return customizations;
    }
}